import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AmortizationTable } from "../components/realestate/AmortizationTable";
import { InterestPhaseFormModal } from "../components/realestate/InterestPhaseFormModal";
import { InterestPhaseList } from "../components/realestate/InterestPhaseList";
import { LoanForm } from "../components/realestate/LoanForm";
import { OneTimeEntryFormModal } from "../components/realestate/OneTimeEntryFormModal";
import { OneTimeEntryList } from "../components/realestate/OneTimeEntryList";
import { OrphanAnnotationCard } from "../components/realestate/OrphanAnnotationCard";
import { PrepaymentFormModal } from "../components/realestate/PrepaymentFormModal";
import { PropertyForm } from "../components/realestate/PropertyForm";
import { RecurringCostList } from "../components/realestate/RecurringCostList";
import { RemainingDebtChart } from "../components/realestate/RemainingDebtChart";
import { RentChangeFormModal } from "../components/realestate/RentChangeFormModal";
import { TenantCard } from "../components/realestate/TenantCard";
import { TenantForm } from "../components/realestate/TenantForm";
import { VacancyAnnotationFormModal } from "../components/realestate/VacancyAnnotationFormModal";
import { VacancyCard } from "../components/realestate/VacancyCard";
import { VacancyStatsCard } from "../components/realestate/VacancyStatsCard";
import { ValuationFormModal } from "../components/realestate/ValuationFormModal";
import { ValuationHistoryChart } from "../components/realestate/ValuationHistoryChart";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Modal } from "../components/ui/Modal";
import { useAccounts } from "../hooks/accounts";
import {
  useAddInterestPhase,
  useAddLoan,
  useAddOneTimeEntry,
  useAddPrepayment,
  useAddRecurringCost,
  useAddRentChange,
  useAddValuation,
  useAmortizationSchedule,
  useCashflow,
  useCashflowOverview,
  useCreateTenant,
  useCreateVacancyAnnotation,
  useDeleteInterestPhase,
  useDeleteOneTimeEntry,
  useDeletePrepayment,
  useDeleteRecurringCost,
  useDeleteRentChange,
  useDeleteVacancyAnnotation,
  useDeleteValuation,
  useEndTenancy,
  useHardDeleteLoan,
  useHardDeleteProperty,
  useHardDeleteTenant,
  useOneTimeEntries,
  useProperty,
  useRecurringCosts,
  useSoftDeleteProperty,
  useUpdateInterestPhase,
  useUpdatePrepayment,
  useUpdateProperty,
  useUpdateTenant,
  useUpdateVacancyAnnotation,
  useUpdateValuation,
  useVacancies,
  useVacancyStatistics,
  useYields,
} from "../hooks/realestate";
import type {
  CreateLoanInput,
  CreateOneTimeEntryInput,
  CreateTenantInput,
  InterestPhase,
  Loan,
  Prepayment,
  PropertyDetail,
  UpdatePrepaymentInput,
  UpdateTenantInput,
  UpdateValuationInput,
  VacancyPeriod,
  VacancyReason,
  Valuation,
} from "../lib/realestate";

const TABS = ["overview", "loans", "tenants", "cashflow"] as const;
type Tab = (typeof TABS)[number];

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function RealEstateDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? "")
    ? (tabParam as Tab)
    : "overview";

  const detail = useProperty(id ?? "");
  const accounts = useAccounts({ active: "true" });
  const update = useUpdateProperty();
  const softDelete = useSoftDeleteProperty();
  const hardDelete = useHardDeleteProperty();
  const addLoan = useAddLoan();
  const hardDeleteLoan = useHardDeleteLoan();
  const addPrepayment = useAddPrepayment();
  const updatePrepayment = useUpdatePrepayment();
  const deletePrepayment = useDeletePrepayment();
  const addValuation = useAddValuation();
  const updateValuation = useUpdateValuation();
  const deleteValuation = useDeleteValuation();

  const [editOpen, setEditOpen] = useState(false);
  const [valuationOpen, setValuationOpen] = useState(false);
  const [editingValuation, setEditingValuation] = useState<Valuation | null>(null);
  const [prepaymentLoanId, setPrepaymentLoanId] = useState<string | null>(null);
  const [editingPrepayment, setEditingPrepayment] = useState<{
    loanId: string;
    prepayment: Prepayment;
  } | null>(null);
  const [confirmHard, setConfirmHard] = useState(false);

  const currentYear = new Date().getUTCFullYear();
  const [cashflowYear, setCashflowYear] = useState(currentYear);

  if (detail.isLoading || !detail.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const property = detail.data;

  function setTab(next: Tab) {
    setSearchParams({ tab: next });
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/realestate" className="hover:underline">
              {t("nav.realestate")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{property.name}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{property.name}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {property.street}, {property.postalCode} {property.city}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditOpen(true)}
          >
            {t("realestate.detail.edit")}
          </button>
          {property.isActive ? (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => softDelete.mutate(property.id)}
            >
              {t("realestate.detail.deactivate")}
            </button>
          ) : (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => update.mutate({ id: property.id, input: { isActive: true } })}
            >
              {t("realestate.detail.activate")}
            </button>
          )}
        </div>
      </div>

      <div className="border-b flex gap-1" style={{ borderColor: "var(--border)" }}>
        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors"
            style={{
              color: tab === k ? "var(--text)" : "var(--muted)",
              borderColor: tab === k ? "var(--accent)" : "transparent",
            }}
          >
            {t(`realestate.tabs.${k}`)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab
          property={property}
          onAddValuation={() => {
            setEditingValuation(null);
            setValuationOpen(true);
          }}
          onEditValuation={(v) => setEditingValuation(v)}
          onDeleteValuation={(vid) =>
            deleteValuation.mutate({ propertyId: property.id, valuationId: vid })
          }
        />
      )}
      {tab === "loans" && (
        <LoansTab
          property={property}
          onAddLoan={(input) => addLoan.mutateAsync({ propertyId: property.id, input })}
          onHardDeleteLoan={(loanId) =>
            hardDeleteLoan.mutateAsync({ propertyId: property.id, loanId })
          }
          onAddPrepayment={(loanId) => setPrepaymentLoanId(loanId)}
          onEditPrepayment={(loanId, prepayment) => setEditingPrepayment({ loanId, prepayment })}
          onDeletePrepayment={(loanId, prepaymentId) =>
            deletePrepayment.mutate({ propertyId: property.id, loanId, prepaymentId })
          }
        />
      )}
      {tab === "tenants" && <TenantsTab property={property} />}
      {tab === "cashflow" && (
        <CashflowTab propertyId={property.id} year={cashflowYear} onYearChange={setCashflowYear} />
      )}

      <section className="border-t pt-4">
        <button
          type="button"
          className="text-red-700 underline disabled:opacity-50"
          onClick={() => setConfirmHard(true)}
          disabled={property.loans.length > 0 || property.valuations.length > 0}
        >
          {t("realestate.detail.hard_delete")}
        </button>
      </section>

      {editOpen && (
        <Modal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={t("realestate.detail.edit")}
        >
          <PropertyForm
            mode="edit"
            initial={property}
            accounts={accounts.data?.accounts ?? []}
            onSubmit={async (input) => {
              await update.mutateAsync({ id: property.id, input });
              setEditOpen(false);
            }}
            submitting={update.isPending}
          />
        </Modal>
      )}

      {(valuationOpen || editingValuation !== null) && (
        <ValuationFormModal
          open={valuationOpen || editingValuation !== null}
          existing={editingValuation}
          submitting={addValuation.isPending || updateValuation.isPending}
          onClose={() => {
            setValuationOpen(false);
            setEditingValuation(null);
          }}
          onSubmit={async (input) => {
            if (editingValuation) {
              await updateValuation.mutateAsync({
                propertyId: property.id,
                valuationId: editingValuation.id,
                input: input as UpdateValuationInput,
              });
              setEditingValuation(null);
            } else {
              await addValuation.mutateAsync({
                propertyId: property.id,
                input: {
                  stichtag: input.stichtag as string,
                  valueCents: input.valueCents as number,
                  source: input.source as Valuation["source"],
                  notes: input.notes ?? undefined,
                },
              });
              setValuationOpen(false);
            }
          }}
        />
      )}

      {(prepaymentLoanId || editingPrepayment) && (
        <PrepaymentFormModal
          open={Boolean(prepaymentLoanId) || editingPrepayment !== null}
          existing={editingPrepayment?.prepayment ?? null}
          submitting={addPrepayment.isPending || updatePrepayment.isPending}
          onClose={() => {
            setPrepaymentLoanId(null);
            setEditingPrepayment(null);
          }}
          onSubmit={async (input) => {
            if (editingPrepayment) {
              await updatePrepayment.mutateAsync({
                propertyId: property.id,
                loanId: editingPrepayment.loanId,
                prepaymentId: editingPrepayment.prepayment.id,
                input: input as UpdatePrepaymentInput,
              });
              setEditingPrepayment(null);
            } else if (prepaymentLoanId) {
              await addPrepayment.mutateAsync({
                propertyId: property.id,
                loanId: prepaymentLoanId,
                input: {
                  date: input.date as string,
                  amountCents: input.amountCents as number,
                  notes: input.notes ?? undefined,
                },
              });
              setPrepaymentLoanId(null);
            }
          }}
        />
      )}

      {confirmHard && (
        <ConfirmDialog
          open
          title={t("realestate.detail.hard_delete_title")}
          message={t("realestate.detail.hard_delete_message", { name: property.name })}
          confirmLabel={t("realestate.detail.hard_delete_confirm")}
          destructive
          onConfirm={async () => {
            await hardDelete.mutateAsync(property.id);
            setConfirmHard(false);
          }}
          onCancel={() => setConfirmHard(false)}
        />
      )}
    </section>
  );
}

function OverviewTab({
  property,
  onAddValuation,
  onEditValuation,
  onDeleteValuation,
}: {
  property: PropertyDetail;
  onAddValuation: () => void;
  onEditValuation: (valuation: Valuation) => void;
  onDeleteValuation: (id: string) => void;
}) {
  const { t } = useTranslation();
  const currentYear = new Date().getUTCFullYear();
  const yields = useYields(property.id, currentYear);
  const cashflow = useCashflowOverview(property.id);
  const o = cashflow.data;

  // Derived values
  const latestValuation = property.valuations[property.valuations.length - 1] ?? null;
  const marketValueCents = latestValuation?.valueCents ?? property.purchasePriceCents;
  const purchaseDiff = marketValueCents - property.purchasePriceCents;
  const totalCostCents = property.purchasePriceCents + property.acquisitionCostsCents;

  // Restschuld = Summe aller Loans (vereinfacht: nutze principalCents als Naeherung,
  // exakt waere aktueller Restbestand via Amortizations-Schedule)
  const remainingDebtCents = property.loans.reduce((s, l) => s + l.principalCents, 0);

  // Buchwert + Restjahre
  const elapsedYears =
    (Date.now() - new Date(property.purchaseDate).getTime()) / (365.25 * 86_400_000);
  const afaPctElapsed = property.afaRateBp > 0 ? (elapsedYears * property.afaRateBp) / 10_000 : 0;
  const monthsSincePurchase = Math.floor(elapsedYears * 12);

  return (
    <div className="space-y-5">
      {/* Stats-Strip: 5 Spalten */}
      <div
        className="fv-card grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x"
        style={{ borderColor: "var(--border)" }}
      >
        <StatMini
          label={t("realestate.detail.stat_market_value", { defaultValue: "Marktwert" })}
          value={formatEur(marketValueCents)}
          sub={
            purchaseDiff !== 0
              ? `${purchaseDiff > 0 ? "+" : ""}${formatEur(purchaseDiff)} ${t("realestate.detail.since_purchase_short", { defaultValue: "seit Kauf" })}`
              : undefined
          }
          subTone={purchaseDiff >= 0 ? "pos" : "neg"}
        />
        <StatMini
          label={t("realestate.detail.stat_equity", { defaultValue: "Eigenkapital" })}
          value={formatEur(property.equityCents)}
          sub={t("realestate.detail.stat_equity_formula", {
            defaultValue: "Marktwert minus Restschuld",
          })}
        />
        <StatMini
          label={t("realestate.detail.stat_remaining_debt", { defaultValue: "Restschuld" })}
          value={formatEur(remainingDebtCents)}
        />
        <StatMini
          label={t("realestate.detail.stat_book_value", { defaultValue: "Buchwert" })}
          value={formatEur(property.afa.bookValueCents)}
          sub={t("realestate.detail.stat_book_value_sub", {
            defaultValue: "nach {{years}} J AfA",
            years: elapsedYears.toFixed(1).replace(".", ","),
          })}
        />
        <StatMini
          label={t("realestate.detail.stat_equity_return", { defaultValue: "EK-Rendite" })}
          value={
            yields.data?.equityReturnBp !== null && yields.data?.equityReturnBp !== undefined
              ? `${(yields.data.equityReturnBp / 100).toFixed(2)} %`
              : "—"
          }
          sub={`${currentYear} p.a.`}
          tone={
            yields.data?.equityReturnBp !== null && yields.data?.equityReturnBp !== undefined
              ? yields.data.equityReturnBp >= 0
                ? "pos"
                : "neg"
              : undefined
          }
          emphasis
        />
      </div>

      {/* Cashflow-Uebersicht als Tabelle */}
      {o && (
        <div className="fv-card overflow-hidden">
          <div
            className="px-5 py-4 border-b"
            style={{ borderColor: "var(--border)" }}
          >
            <h3 className="text-base font-semibold">
              {t("realestate.cashflow_overview.title", { defaultValue: "Cashflow-Übersicht" })}
            </h3>
            <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
              {t("realestate.cashflow_overview.bank_net_explainer", {
                defaultValue:
                  "Bank-Netto = Liquiditätswirkung · Steuer-Netto = Einkünfte vor AfA, ohne Tilgung",
              })}{" "}
              · {monthsSincePurchase}{" "}
              {t("realestate.cashflow_overview.months_since_purchase_short", {
                defaultValue: "Monate seit Kauf",
              })}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-xs"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                <th className="px-4 py-2 text-left font-semibold">
                  {t("realestate.cashflow_overview.position", { defaultValue: "Position" })}
                </th>
                <th className="px-4 py-2 text-right font-semibold">
                  {t("realestate.cashflow_overview.current_month_short", {
                    defaultValue: "Aktueller Monat",
                  })}
                  <div className="font-normal text-[10px]" style={{ color: "var(--subtle)" }}>
                    {o.currentMonth}
                  </div>
                </th>
                <th className="px-4 py-2 text-right font-semibold">
                  {t("realestate.cashflow_overview.ytd", { defaultValue: "YTD" })}
                  <div className="font-normal text-[10px]" style={{ color: "var(--subtle)" }}>
                    {currentYear}
                  </div>
                </th>
                <th className="px-4 py-2 text-right font-semibold">
                  {t("realestate.cashflow_overview.lifetime", { defaultValue: "Seit Kauf" })}
                  <div className="font-normal text-[10px]" style={{ color: "var(--subtle)" }}>
                    {property.purchaseDate.slice(0, 7)}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <CfRow
                label={t("realestate.cashflow_overview.tax_net", { defaultValue: "Steuer-Netto" })}
                sub={t("realestate.cashflow_overview.tax_net_sub", {
                  defaultValue: "ohne Tilgung & AfA",
                })}
                month={o.monthNetCents}
                ytd={o.ytdNetCents}
                lifetime={o.lifetimeNetCents}
              />
              <CfRow
                label={t("realestate.cashflow_overview.bank_net", { defaultValue: "Bank-Netto" })}
                sub={t("realestate.cashflow_overview.bank_net_sub", {
                  defaultValue: "Liquidität",
                })}
                month={o.monthBankNetCents}
                ytd={o.ytdBankNetCents}
                lifetime={o.lifetimeBankNetCents}
                highlight
              />
            </tbody>
          </table>
        </div>
      )}

      {/* Eckdaten + AfA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="fv-card p-5">
          <h3 className="text-base font-semibold mb-4">
            {t("realestate.detail.eckdaten_title", { defaultValue: "Eckdaten Anschaffung" })}
          </h3>
          <table className="w-full text-sm">
            <tbody>
              <EckdatenRow
                label={t("realestate.detail.purchase_price")}
                value={formatEur(property.purchasePriceCents)}
              />
              <EckdatenRow
                label={t("realestate.detail.acquisition_costs")}
                value={formatEur(property.acquisitionCostsCents)}
              />
              <EckdatenRow
                label={t("realestate.detail.total_cost", { defaultValue: "Gesamtkosten" })}
                value={formatEur(totalCostCents)}
                bold
              />
              <EckdatenRow
                label={t("realestate.detail.building_share")}
                value={`${(property.buildingShareBp / 100).toFixed(2)} %`}
              />
              <EckdatenRow
                label={t("realestate.detail.afa_rate")}
                value={`${(property.afaRateBp / 100).toFixed(2)} % p.a.`}
              />
              <EckdatenRow
                label={t("realestate.detail.equity")}
                value={formatEur(property.equityCents)}
              />
            </tbody>
          </table>
        </div>

        <div className="fv-card p-5">
          <h3 className="text-base font-semibold mb-4">{t("realestate.detail.afa_block")}</h3>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div>
              <div className="fv-kpi-label mb-1">{t("realestate.detail.afa_annual")}</div>
              <div className="text-lg font-bold num">{formatEur(property.afa.annualAfaCents)}</div>
            </div>
            <div>
              <div className="fv-kpi-label mb-1">{t("realestate.detail.afa_accumulated")}</div>
              <div className="text-lg font-bold num">{formatEur(property.afa.accumulatedAfaCents)}</div>
            </div>
            <div>
              <div className="fv-kpi-label mb-1">{t("realestate.detail.afa_book_value")}</div>
              <div className="text-lg font-bold num">{formatEur(property.afa.bookValueCents)}</div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1.5" style={{ color: "var(--muted)" }}>
              <span>{t("realestate.detail.afa_progress", { defaultValue: "AfA-Fortschritt" })}</span>
              <span className="num">
                {(afaPctElapsed * 100).toFixed(1)} %{" "}
                {t("realestate.detail.afa_of", { defaultValue: "von" })}{" "}
                {property.afaRateBp > 0 ? (10_000 / property.afaRateBp).toFixed(0) : "—"}{" "}
                {t("realestate.detail.years_short", { defaultValue: "Jahren" })}
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "var(--border-soft)" }}
            >
              <div
                style={{
                  width: `${Math.min(100, afaPctElapsed * 100).toFixed(1)}%`,
                  height: "100%",
                  background: "var(--accent)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Marktwert + Rendite */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="fv-card p-5 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold">
                {t("realestate.detail.valuations", { defaultValue: "Marktwert-Verlauf" })}
              </h3>
              {property.valuations.length === 2 && (
                <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                  {t("realestate.detail.valuations_few", {
                    defaultValue: "Nur 2 Datenpunkte erfasst",
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border text-xs font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={onAddValuation}
            >
              + {t("realestate.detail.add_valuation")}
            </button>
          </div>
          <ValuationHistoryChart valuations={property.valuations} />
          <ul className="mt-4 space-y-1.5 text-xs">
            {property.valuations.map((v: Valuation) => (
              <li
                key={v.id}
                className="flex justify-between items-center py-1.5 border-b last:border-b-0"
                style={{ borderColor: "var(--border-soft)" }}
              >
                <span style={{ color: "var(--muted)" }}>
                  {v.stichtag} — {v.source}
                </span>
                <span className="num font-semibold">{formatEur(v.valueCents)}</span>
                <span className="flex gap-3 text-[11px]">
                  <button
                    type="button"
                    style={{ color: "var(--accent)" }}
                    onClick={() => onEditValuation(v)}
                  >
                    {t("realestate.valuation.edit_button")}
                  </button>
                  <button
                    type="button"
                    style={{ color: "var(--neg)" }}
                    onClick={() => onDeleteValuation(v.id)}
                  >
                    {t("common.delete")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="fv-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold">
              {t("realestate.yield.section_title", { defaultValue: "Rendite" })}
            </h3>
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {currentYear}
            </span>
          </div>
          <div className="space-y-4">
            <YieldRow
              label={t("realestate.yield.brutto", { defaultValue: "Brutto-Rendite" })}
              valueBp={yields.data?.bruttoBp ?? null}
              hint={t("realestate.yield.brutto_hint", {
                defaultValue: "Kaltmiete / Kaufpreis + Nebenkosten",
              })}
            />
            <YieldRow
              label={t("realestate.yield.netto", { defaultValue: "Netto-Rendite" })}
              valueBp={yields.data?.nettoBp ?? null}
              hint={t("realestate.yield.netto_hint", {
                defaultValue: "(Miete − Bewirtsch.) / Kaufpreis + Nebenkosten",
              })}
            />
            <div className="pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <YieldRow
                label={t("realestate.yield.equity_return", { defaultValue: "EK-Rendite" })}
                valueBp={yields.data?.equityReturnBp ?? null}
                hint={t("realestate.yield.equity_return_hint", {
                  defaultValue: "(Miete − Kosten − Zins) / Eigenkapital",
                })}
                highlight
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatMini({
  label,
  value,
  sub,
  subTone,
  tone,
  emphasis,
}: {
  label: string;
  value: string;
  sub?: string;
  subTone?: "pos" | "neg";
  tone?: "pos" | "neg";
  emphasis?: boolean;
}) {
  const color = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  const subColor =
    subTone === "pos" ? "var(--pos)" : subTone === "neg" ? "var(--neg)" : "var(--muted)";
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1 num" style={{ color: subColor }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function CfRow({
  label,
  sub,
  month,
  ytd,
  lifetime,
  highlight,
}: {
  label: string;
  sub?: string;
  month: number;
  ytd: number;
  lifetime: number;
  highlight?: boolean;
}) {
  const tone = (v: number) => (v >= 0 ? "var(--pos)" : "var(--neg)");
  return (
    <tr
      className="border-b"
      style={{
        borderColor: "var(--border-soft)",
        background: highlight ? "var(--accent-soft)" : undefined,
        borderTop: highlight ? "2px solid var(--accent)" : undefined,
      }}
    >
      <td className="px-4 py-3">
        <div className={`text-sm ${highlight ? "font-bold" : "font-medium"}`}>{label}</div>
        {sub && (
          <div className="text-xs" style={{ color: "var(--muted)" }}>
            {sub}
          </div>
        )}
      </td>
      <td
        className={`px-4 py-3 text-right num ${highlight ? "font-bold" : "font-semibold"}`}
        style={{ color: tone(month) }}
      >
        {formatEur(month)}
      </td>
      <td
        className={`px-4 py-3 text-right num ${highlight ? "font-bold" : "font-semibold"}`}
        style={{ color: tone(ytd) }}
      >
        {formatEur(ytd)}
      </td>
      <td
        className={`px-4 py-3 text-right num ${highlight ? "font-bold" : "font-semibold"}`}
        style={{ color: tone(lifetime) }}
      >
        {formatEur(lifetime)}
      </td>
    </tr>
  );
}

function EckdatenRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <tr>
      <td className="py-1.5 text-sm" style={{ color: "var(--muted)" }}>
        {label}
      </td>
      <td
        className={`py-1.5 text-right num ${bold ? "font-bold" : "font-medium"}`}
        style={{ color: "var(--text)" }}
      >
        {value}
      </td>
    </tr>
  );
}

function YieldRow({
  label,
  valueBp,
  hint,
  highlight,
}: {
  label: string;
  valueBp: number | null;
  hint?: string;
  highlight?: boolean;
}) {
  const valueColor = highlight
    ? "var(--accent)"
    : valueBp !== null && valueBp < 0
      ? "var(--neg)"
      : "var(--pos)";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: highlight ? "var(--accent)" : "var(--muted)" }}
        >
          {label}
        </span>
        <span
          className={`num ${highlight ? "text-xl font-bold" : "text-base font-bold"}`}
          style={{ color: valueColor }}
        >
          {valueBp !== null ? `${(valueBp / 100).toFixed(2)} %` : "—"}
        </span>
      </div>
      {hint && (
        <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function LoansTab(props: {
  property: PropertyDetail;
  onAddLoan: (input: CreateLoanInput) => Promise<unknown>;
  onHardDeleteLoan: (loanId: string) => Promise<unknown>;
  onAddPrepayment: (loanId: string) => void;
  onEditPrepayment: (loanId: string, prepayment: Prepayment) => void;
  onDeletePrepayment: (loanId: string, prepaymentId: string) => void;
}) {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  return (
    <div className="space-y-4">
      <header className="flex justify-end">
        <button type="button" className="px-3 py-1 border rounded" onClick={() => setAddOpen(true)}>
          {t("realestate.detail.add_loan")}
        </button>
      </header>
      {addOpen && (
        <Modal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title={t("realestate.detail.add_loan")}
        >
          <LoanForm
            mode="create"
            onSubmit={async (input) => {
              await props.onAddLoan(input as CreateLoanInput);
              setAddOpen(false);
            }}
          />
        </Modal>
      )}
      {props.property.loans.map((loan) => (
        <LoanCard
          key={loan.id}
          propertyId={props.property.id}
          loan={loan}
          onHardDelete={() => props.onHardDeleteLoan(loan.id)}
          onAddPrepayment={() => props.onAddPrepayment(loan.id)}
          onEditPrepayment={(p) => props.onEditPrepayment(loan.id, p)}
          onDeletePrepayment={(pid) => props.onDeletePrepayment(loan.id, pid)}
        />
      ))}
    </div>
  );
}

function LoanCard({
  propertyId,
  loan,
  onHardDelete,
  onAddPrepayment,
  onEditPrepayment,
  onDeletePrepayment,
}: {
  propertyId: string;
  loan: Loan & { prepayments: Prepayment[] };
  onHardDelete: () => Promise<unknown>;
  onAddPrepayment: () => void;
  onEditPrepayment: (prepayment: Prepayment) => void;
  onDeletePrepayment: (id: string) => void;
}) {
  const { t } = useTranslation();
  const scheduleQuery = useAmortizationSchedule(propertyId, loan.id);
  const overrides = scheduleQuery.data?.overrides ?? [];
  const schedule = scheduleQuery.data?.schedule ?? [];
  const addPhase = useAddInterestPhase();
  const updatePhase = useUpdateInterestPhase();
  const deletePhase = useDeleteInterestPhase();
  const [phaseModal, setPhaseModal] = useState<{ existing: InterestPhase | null } | null>(null);
  const [phaseToDelete, setPhaseToDelete] = useState<InterestPhase | null>(null);

  return (
    <div className="bg-white border rounded p-4 space-y-3">
      <header className="flex items-center justify-between">
        <h3 className="font-semibold">{loan.name}</h3>
        <div className="flex gap-2">
          <button
            type="button"
            className="text-sm px-3 py-1 border rounded"
            onClick={onAddPrepayment}
          >
            {t("realestate.detail.add_prepayment")}
          </button>
          <button type="button" className="text-sm text-red-700" onClick={onHardDelete}>
            {t("common.delete")}
          </button>
        </div>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <Field label={t("realestate.loan.principal")} value={formatEur(loan.principalCents)} />
        <Field
          label={t("realestate.loan.interest_rate")}
          value={`${(loan.annualInterestRateBp / 100).toFixed(2)} %`}
        />
        <Field
          label={t("realestate.loan.monthly_payment")}
          value={formatEur(loan.monthlyPaymentCents)}
        />
        <Field label={t("realestate.loan.start_date")} value={loan.startDate} />
      </div>
      <InterestPhaseList
        loan={loan}
        phases={loan.interestPhases}
        onAdd={() => setPhaseModal({ existing: null })}
        onEdit={(p) => setPhaseModal({ existing: p })}
        onDelete={(p) => setPhaseToDelete(p)}
      />
      {loan.prepayments.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold mb-1">{t("realestate.detail.prepayments")}</h4>
          <ul className="text-sm space-y-1">
            {loan.prepayments.map((p) => (
              <li key={p.id} className="flex justify-between border-b pb-1">
                <span>{p.date}</span>
                <span className="font-mono">{formatEur(p.amountCents)}</span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-blue-600 underline"
                    onClick={() => onEditPrepayment(p)}
                  >
                    {t("realestate.prepayment.edit_button")}
                  </button>
                  <button
                    type="button"
                    className="text-red-700 text-xs"
                    onClick={() => onDeletePrepayment(p.id)}
                  >
                    {t("common.delete")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {schedule.length > 0 && (
        <>
          <RemainingDebtChart schedule={schedule} />
          <AmortizationTable
            propertyId={propertyId}
            loanId={loan.id}
            schedule={schedule}
            overrides={overrides}
            interestPhases={loan.interestPhases}
          />
        </>
      )}
      {phaseModal && (
        <InterestPhaseFormModal
          open
          existing={phaseModal.existing}
          loanStartDate={loan.startDate}
          submitting={addPhase.isPending || updatePhase.isPending}
          onClose={() => setPhaseModal(null)}
          onSubmit={async (input) => {
            if (phaseModal.existing) {
              await updatePhase.mutateAsync({
                propertyId,
                loanId: loan.id,
                phaseId: phaseModal.existing.id,
                input,
              });
            } else {
              await addPhase.mutateAsync({
                propertyId,
                loanId: loan.id,
                input: {
                  startDate: input.startDate,
                  annualInterestRateBp: input.annualInterestRateBp,
                  monthlyPaymentCents: input.monthlyPaymentCents,
                  notes: input.notes ?? undefined,
                },
              });
            }
            setPhaseModal(null);
          }}
        />
      )}
      {phaseToDelete && (
        <ConfirmDialog
          open
          title={t("realestate.loan.interest_phase.delete_confirm_title")}
          message={t("realestate.loan.interest_phase.delete_confirm_message", {
            date: phaseToDelete.startDate,
          })}
          confirmLabel={t("realestate.loan.interest_phase.delete_confirm_ok")}
          destructive
          onConfirm={async () => {
            const id = phaseToDelete.id;
            setPhaseToDelete(null);
            await deletePhase.mutateAsync({ propertyId, loanId: loan.id, phaseId: id });
          }}
          onCancel={() => setPhaseToDelete(null)}
        />
      )}
    </div>
  );
}

function TenantsTab({ property }: { property: PropertyDetail }) {
  const { t } = useTranslation();
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const endTenancy = useEndTenancy();
  const hardDeleteTenant = useHardDeleteTenant();
  const addRentChange = useAddRentChange();
  const deleteRentChange = useDeleteRentChange();
  const [addOpen, setAddOpen] = useState(false);
  const [editTenantId, setEditTenantId] = useState<string | null>(null);
  const [rentChangeTenantId, setRentChangeTenantId] = useState<string | null>(null);
  const [hardDeleteTenantId, setHardDeleteTenantId] = useState<string | null>(null);
  const vacanciesQuery = useVacancies(property.id);
  const currentYear = new Date().getUTCFullYear();
  const purchaseYear = Number(property.purchaseDate.slice(0, 4));
  const availableYears = Array.from(
    { length: currentYear - purchaseYear + 1 },
    (_, i) => currentYear - i,
  );
  const [statsYear, setStatsYear] = useState(currentYear);
  const statsQuery = useVacancyStatistics(property.id, statsYear);
  const createAnnotation = useCreateVacancyAnnotation();
  const updateAnnotation = useUpdateVacancyAnnotation();
  const deleteAnnotation = useDeleteVacancyAnnotation();
  const [annotationModal, setAnnotationModal] = useState<{
    unitLabel: string;
    startMonth: string;
    existing: VacancyPeriod["annotation"];
  } | null>(null);

  const active = property.tenants.filter((tn) => tn.moveOutDate === null);
  const historical = property.tenants.filter((tn) => tn.moveOutDate !== null);
  const editTenant = property.tenants.find((tn) => tn.id === editTenantId);

  return (
    <div className="space-y-4">
      <header className="flex justify-end">
        <button
          type="button"
          className="px-3 py-1 bg-blue-600 text-white rounded"
          onClick={() => setAddOpen(true)}
        >
          {t("realestate.tenant.new")}
        </button>
      </header>

      {active.length === 0 && historical.length === 0 && (
        <div className="bg-white border rounded p-6 text-center text-slate-600">
          <p className="mb-3">{t("realestate.tenant.empty")}</p>
        </div>
      )}

      {active.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700">
            {t("realestate.tenant.active_section")}
          </h2>
          {active.map((tenant) => (
            <TenantCard
              key={tenant.id}
              propertyId={property.id}
              tenant={tenant}
              onAddRentChange={() => setRentChangeTenantId(tenant.id)}
              onEndTenancy={async () => {
                await endTenancy.mutateAsync({ propertyId: property.id, tenantId: tenant.id });
              }}
              onEdit={() => setEditTenantId(tenant.id)}
              onDeleteRentChange={(rcId) =>
                deleteRentChange.mutate({
                  propertyId: property.id,
                  tenantId: tenant.id,
                  rentChangeId: rcId,
                })
              }
            />
          ))}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">
          {t("realestate.vacancy.section_title")}
        </h2>
        <p className="text-xs text-slate-500">{t("realestate.vacancy.unit_hint")}</p>

        <VacancyStatsCard
          stats={statsQuery.data}
          year={statsYear}
          availableYears={availableYears}
          onYearChange={setStatsYear}
        />

        {vacanciesQuery.data?.periods.map((v) => (
          <VacancyCard
            key={`${v.unitLabel}-${v.startMonth}`}
            vacancy={v}
            onAddReason={() =>
              setAnnotationModal({
                unitLabel: v.unitLabel,
                startMonth: v.startMonth,
                existing: null,
              })
            }
            onEdit={() =>
              setAnnotationModal({
                unitLabel: v.unitLabel,
                startMonth: v.startMonth,
                existing: v.annotation,
              })
            }
            onDelete={() => {
              if (v.annotation) {
                void deleteAnnotation.mutateAsync(v.annotation.id);
              }
            }}
          />
        ))}

        {vacanciesQuery.data?.orphanAnnotations.length ? (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-amber-700">
              {t("realestate.vacancy.orphan_section_title")}
            </h3>
            <p className="text-xs text-slate-500">{t("realestate.vacancy.orphan_hint")}</p>
            {vacanciesQuery.data.orphanAnnotations.map((a) => (
              <OrphanAnnotationCard
                key={a.id}
                annotation={a}
                onDelete={() => {
                  void deleteAnnotation.mutateAsync(a.id);
                }}
              />
            ))}
          </div>
        ) : null}
      </section>

      {historical.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700">
            {t("realestate.tenant.historical_section")}
          </h2>
          {historical.map((tenant) => (
            <TenantCard
              key={tenant.id}
              propertyId={property.id}
              tenant={tenant}
              onAddRentChange={() => setRentChangeTenantId(tenant.id)}
              onEndTenancy={async () => {
                await endTenancy.mutateAsync({ propertyId: property.id, tenantId: tenant.id });
              }}
              onEdit={() => setEditTenantId(tenant.id)}
              onHardDelete={() => setHardDeleteTenantId(tenant.id)}
              onDeleteRentChange={(rcId) =>
                deleteRentChange.mutate({
                  propertyId: property.id,
                  tenantId: tenant.id,
                  rentChangeId: rcId,
                })
              }
            />
          ))}
        </section>
      )}

      {addOpen && (
        <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t("realestate.tenant.new")}>
          <TenantForm
            mode="create"
            onSubmit={async (input) => {
              await createTenant.mutateAsync({
                propertyId: property.id,
                input: input as CreateTenantInput,
              });
              setAddOpen(false);
            }}
            submitting={createTenant.isPending}
          />
        </Modal>
      )}

      {editTenant && (
        <Modal
          open={Boolean(editTenantId)}
          onClose={() => setEditTenantId(null)}
          title={t("realestate.tenant.edit")}
        >
          <TenantForm
            mode="edit"
            initial={editTenant}
            onSubmit={async (input) => {
              await updateTenant.mutateAsync({
                propertyId: property.id,
                tenantId: editTenant.id,
                input: input as UpdateTenantInput,
              });
              setEditTenantId(null);
            }}
            submitting={updateTenant.isPending}
          />
        </Modal>
      )}

      {rentChangeTenantId && (
        <RentChangeFormModal
          open={Boolean(rentChangeTenantId)}
          onClose={() => setRentChangeTenantId(null)}
          onSubmit={async (input) => {
            if (!rentChangeTenantId) return;
            await addRentChange.mutateAsync({
              propertyId: property.id,
              tenantId: rentChangeTenantId,
              input,
            });
            setRentChangeTenantId(null);
          }}
        />
      )}

      {annotationModal && (
        <VacancyAnnotationFormModal
          open={true}
          onClose={() => setAnnotationModal(null)}
          unitLabel={annotationModal.unitLabel}
          startMonth={annotationModal.startMonth}
          existing={annotationModal.existing}
          submitting={createAnnotation.isPending || updateAnnotation.isPending}
          onSubmit={async ({ reason, notes }) => {
            if (annotationModal.existing) {
              await updateAnnotation.mutateAsync({
                annotationId: annotationModal.existing.id,
                input: { reason, notes },
              });
            } else {
              await createAnnotation.mutateAsync({
                propertyId: property.id,
                input: {
                  unitLabel: annotationModal.unitLabel,
                  startMonth: annotationModal.startMonth,
                  reason: reason as VacancyReason,
                  notes: notes ?? undefined,
                },
              });
            }
            setAnnotationModal(null);
          }}
        />
      )}

      {hardDeleteTenantId && (
        <ConfirmDialog
          open
          title={t("realestate.tenant.hard_delete_confirm_title")}
          message={t("realestate.tenant.hard_delete_confirm_message", {
            name: property.tenants.find((tn) => tn.id === hardDeleteTenantId)?.name ?? "",
          })}
          confirmLabel={t("realestate.tenant.hard_delete_confirm_ok")}
          destructive
          onConfirm={async () => {
            const tenantId = hardDeleteTenantId;
            setHardDeleteTenantId(null);
            if (tenantId) await hardDeleteTenant.mutateAsync({ propertyId: property.id, tenantId });
          }}
          onCancel={() => setHardDeleteTenantId(null)}
        />
      )}
    </div>
  );
}

function CashflowTab({
  propertyId,
  year,
  onYearChange,
}: { propertyId: string; year: number; onYearChange: (y: number) => void }) {
  const { t } = useTranslation();
  const cashflow = useCashflow(propertyId, year);
  const recurringCosts = useRecurringCosts(propertyId);
  const oneTimeEntries = useOneTimeEntries(propertyId);
  const addRecurring = useAddRecurringCost();
  const deleteRecurring = useDeleteRecurringCost();
  const addOneTime = useAddOneTimeEntry();
  const deleteOneTime = useDeleteOneTimeEntry();
  const [oneTimeModalOpen, setOneTimeModalOpen] = useState(false);

  if (!cashflow.data || !recurringCosts.data || !oneTimeEntries.data) return <p>...</p>;

  const totals = cashflow.data.months.reduce(
    (sum, m) => ({
      coldRent: sum.coldRent + m.cashflow.coldRentCents,
      expense: sum.expense + m.cashflow.expenseCents,
      interest: sum.interest + m.cashflow.interestCents,
      principal: sum.principal + m.cashflow.principalCents,
      oneTimeIncome: sum.oneTimeIncome + m.cashflow.oneTimeIncomeCents,
      oneTimeExpense: sum.oneTimeExpense + m.cashflow.oneTimeExpenseCents,
      net: sum.net + m.cashflow.netCents,
      bankNet: sum.bankNet + m.cashflow.bankNetCents,
    }),
    {
      coldRent: 0,
      expense: 0,
      interest: 0,
      principal: 0,
      oneTimeIncome: 0,
      oneTimeExpense: 0,
      net: 0,
      bankNet: 0,
    },
  );

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-2">
        <label className="text-sm flex items-center gap-2">
          {t("realestate.cashflow.year")}:
          <select
            className="border rounded px-2 py-1"
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getUTCFullYear() - 5 + i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </header>

      <section className="bg-white border rounded p-4">
        <h3 className="font-semibold mb-3">{t("realestate.recurring.section_title")}</h3>
        <RecurringCostList
          propertyId={propertyId}
          costs={recurringCosts.data.recurringCosts}
          addPending={addRecurring.isPending}
          onAdd={async (input) => {
            await addRecurring.mutateAsync({ propertyId, input });
          }}
          onDelete={(costId) => deleteRecurring.mutate({ propertyId, costId })}
        />
      </section>

      <section className="bg-white border rounded p-4">
        <header className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{t("realestate.one_time.section_title")}</h3>
          <button
            type="button"
            className="px-3 py-1 border rounded text-sm"
            onClick={() => setOneTimeModalOpen(true)}
          >
            {t("realestate.one_time.add")}
          </button>
        </header>
        <OneTimeEntryList
          propertyId={propertyId}
          entries={oneTimeEntries.data.oneTimeEntries}
          onDelete={(entryId) => deleteOneTime.mutate({ propertyId, entryId })}
        />
      </section>

      <section className="bg-white border rounded p-4">
        <h3 className="font-semibold mb-3">{t("realestate.cashflow.monthly_title", { year })}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b">
                <th className="p-2 sticky left-0 bg-white">{t("realestate.cashflow.month")}</th>
                <th className="p-2 text-right sticky left-[80px] bg-white">
                  {t("realestate.cashflow.net")}
                </th>
                <th className="p-2 text-right sticky left-[180px] bg-white">
                  {t("realestate.cashflow.bank_net")}
                </th>
                <th className="p-2 text-right">{t("realestate.monthly.cold_rent")}</th>
                <th className="p-2 text-right">{t("realestate.monthly.side_costs_advance")}</th>
                <th className="p-2 text-right">
                  {t("realestate.recurring.category_label.HAUSGELD")}
                </th>
                <th className="p-2 text-right">
                  {t("realestate.recurring.category_label.VERSICHERUNG")}
                </th>
                <th className="p-2 text-right">
                  {t("realestate.recurring.category_label.INSTANDHALTUNG")}
                </th>
                <th className="p-2 text-right">
                  {t("realestate.recurring.category_label.GRUNDSTEUER")}
                </th>
                <th className="p-2 text-right">
                  {t("realestate.recurring.category_label.SONSTIGES")}
                </th>
                <th className="p-2 text-right">{t("realestate.cashflow.interest")}</th>
                <th className="p-2 text-right">{t("realestate.cashflow.principal")}</th>
                <th className="p-2 text-right">{t("realestate.cashflow.one_time_net")}</th>
              </tr>
            </thead>
            <tbody>
              {cashflow.data.months.map((m) => (
                <tr key={m.month} className="border-b">
                  <td className="p-2 sticky left-0 bg-white font-mono text-xs">{m.month}</td>
                  <td
                    className={`p-2 text-right font-mono sticky left-[80px] bg-white ${m.cashflow.netCents >= 0 ? "text-green-700" : "text-red-700"}`}
                  >
                    {formatEur(m.cashflow.netCents)}
                  </td>
                  <td
                    className={`p-2 text-right font-mono sticky left-[180px] bg-white ${m.cashflow.bankNetCents >= 0 ? "text-green-700" : "text-red-700"}`}
                  >
                    {formatEur(m.cashflow.bankNetCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.coldRentCents)}
                  </td>
                  <td className="p-2 text-right font-mono text-slate-500">
                    {formatEur(m.cashflow.sideCostsAdvanceCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.hausgeldCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.versicherungCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.instandhaltungCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.grundsteuerCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.sonstigesCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.interestCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.principalCents)}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatEur(m.cashflow.oneTimeIncomeCents - m.cashflow.oneTimeExpenseCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-slate-50 border rounded p-4">
        <h3 className="font-semibold mb-2">{t("realestate.cashflow.annual_total")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Field label={t("realestate.cashflow.total_rent")} value={formatEur(totals.coldRent)} />
          <Field label={t("realestate.cashflow.total_expense")} value={formatEur(totals.expense)} />
          <Field
            label={t("realestate.cashflow.total_interest")}
            value={formatEur(totals.interest)}
          />
          <Field
            label={t("realestate.cashflow.total_principal")}
            value={formatEur(totals.principal)}
          />
          <Field
            label={t("realestate.cashflow.total_one_time")}
            value={formatEur(totals.oneTimeIncome - totals.oneTimeExpense)}
          />
          <Field label={t("realestate.cashflow.net")} value={formatEur(totals.net)} />
          <Field label={t("realestate.cashflow.bank_net")} value={formatEur(totals.bankNet)} />
        </div>
      </section>

      {oneTimeModalOpen && (
        <OneTimeEntryFormModal
          open={oneTimeModalOpen}
          existing={null}
          submitting={addOneTime.isPending}
          onClose={() => setOneTimeModalOpen(false)}
          onSubmit={async (input) => {
            await addOneTime.mutateAsync({
              propertyId,
              input: {
                date: input.date as string,
                amountCents: input.amountCents as number,
                category: input.category as CreateOneTimeEntryInput["category"],
                description: input.description as string,
                notes: input.notes ?? undefined,
              },
            });
          }}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="fv-kpi-label">{label}</p>
      <p className="num mt-1 text-sm">{value}</p>
    </div>
  );
}
